import {useEffect, useRef, useState, type MouseEvent} from 'react';
import {InputBase, Stack, type SxProps, type Theme, Typography, type TypographyProps} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {COLORS} from '~/style/colors-ts';
import {observer} from 'mobx-react-lite';
import {authStore} from '~/stores/init';
import {ConfirmChangeDialog} from '~/components/ClickToEdit/ConfirmChangeDialog';

export type ClickToEditProps = {
    text: string;
    /**
     * Called when editing is committed (Enter or blur; Ctrl/Cmd+Enter for paragraphs). With
     * `confirmBeforeSave`, returning a promise keeps the confirm dialog open, loading, until it settles.
     */
    onSave?: (value: string) => unknown;
    typographyProps?: TypographyProps;
    inputSx?: SxProps<Theme>;
    className?: string;
    placeholder?: string;
    /** When true, shows wrapping text and a larger multiline input. */
    isParagraph?: boolean;
    /**
     * When true, only shows the text — no hover state, no edit icon, clicking does nothing.
     * Read-only accounts always get this, whatever is passed; the prop is for locking a field for other reasons.
     */
    readOnly?: boolean;
    /** When true, a committed change first opens a dialog asking to confirm "old" → "new"; cancelling reverts it. */
    confirmBeforeSave?: boolean;
    /** When true, an empty (or whitespace-only) value can't be saved: the confirm button is disabled, or the edit is dropped. */
    required?: boolean;
    sx?: SxProps<Theme>;
};

export const ClickToEdit = observer(function ClickToEdit({
                                                             text,
                                                             onSave,
                                                             inputSx,
                                                             typographyProps,
                                                             className,
                                                             placeholder,
                                                             isParagraph = false,
                                                             readOnly: readOnlyProp = false,
                                                             confirmBeforeSave = false,
                                                             required = false,
                                                             sx
                                                         }: ClickToEditProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(text);
    const [hovered, setHovered] = useState(false);
    /** Change waiting for confirmation (only with `confirmBeforeSave`); `from` is kept so the dialog doesn't change mid-save */
    const [pending, setPending] = useState<{from: string, to: string} | null>(null);
    /** The confirmed change is being saved (an `onSave` that returned a promise) */
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    // Set once Enter or Escape ended the edit. Ending it unmounts the focused InputBase, which fires a native blur;
    // onBlur={commit} would otherwise save the draft after Escape, or save it a second time after Enter.
    const finished = useRef(false);
    const readOnly = readOnlyProp || !authStore.hasEditPermission;

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    const startEditing = () => {
        if (readOnly) return;
        setDraft(text);
        finished.current = false;
        setEditing(true);
    };

    const commit = () => {
        if (finished.current) return;
        finished.current = true;
        setEditing(false);
        setHovered(false);
        // Without a confirm step there is nowhere to block an empty required value: keep the old one.
        if (draft !== text && !(required && !draft.trim() && !confirmBeforeSave)) {
            if (confirmBeforeSave) {
                setPending({from: text, to: draft});
            } else {
                onSave?.(draft);
            }
        }
    };

    const cancelPending = () => {
        setPending(null);
        setDraft(text);
    };

    const confirmPending = () => {
        if (pending === null) return;
        const result = onSave?.(pending.to);
        if (!(result instanceof Promise)) {
            setPending(null);
            return;
        }
        // Stay open until the save is done; either way the field then shows the current `text`.
        setSaving(true);
        const done = () => {
            setSaving(false);
            cancelPending();
        };
        result.then(done, done);
    };

    const confirmDialog = confirmBeforeSave && (
        <ConfirmChangeDialog
            open={pending !== null}
            from={pending?.from ?? text}
            to={pending?.to ?? ''}
            isSaving={saving}
            confirmDisabled={required && !pending?.to.trim()}
            onConfirm={confirmPending}
            onCancel={cancelPending}
        />
    );

    const cancel = () => {
        finished.current = true;
        setDraft(text);
        setEditing(false);
        setHovered(false);
    };

    const stopPropagation = (e: MouseEvent) => {
        e.stopPropagation();
    };

    if (editing) {
        return (<>
            <InputBase
                inputRef={inputRef}
                value={draft}
                placeholder={placeholder}
                multiline={isParagraph}
                minRows={isParagraph ? 4 : undefined}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                onKeyDown={e => {
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        cancel();
                        return;
                    }
                    if (e.key === 'Enter') {
                        if (isParagraph) {
                            // Ctrl/Cmd+Enter commits; plain Enter inserts a newline
                            if (e.ctrlKey || e.metaKey) {
                                e.preventDefault();
                                commit();
                            }
                            return;
                        }
                        e.preventDefault();
                        commit();
                    }
                }}
                className={className}
                sx={[
                    {
                        width: '100%',
                        px: 0.5,
                        py: 0.25,
                        border: `1px solid ${COLORS.info.main}`,
                        borderRadius: 1,
                        fontSize: 'inherit',
                        alignItems: isParagraph ? 'flex-start' : 'center',
                    },
                    isParagraph && {
                        minHeight: 96,
                        '& textarea': {
                            resize: 'vertical',
                        },
                    },
                    inputSx,
                    typographyProps?.sx,
                ] as SxProps<Theme>}
            />
            {confirmDialog}
        </>);
    }

    return (<>
        <Stack
            component="span"
            direction="row"
            alignItems={isParagraph ? 'flex-start' : 'center'}
            gap={0.5}
            className={className}
            onMouseEnter={() => !readOnly && setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={e => {
                stopPropagation(e);
                startEditing();
            }}
            onMouseDown={stopPropagation}
            sx={{
                display: isParagraph ? 'flex' : 'inline-flex',
                width: isParagraph ? '100%' : undefined,
                maxWidth: '100%',
                cursor: readOnly ? 'default' : 'pointer',
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                border: `1px solid ${hovered ? COLORS.grey[200] : 'transparent'}`,
                bgcolor: hovered ? COLORS.grey[100] : 'transparent',
                ...sx,
            }}
        >
            <Typography
                component={isParagraph ? 'p' : 'span'}
                noWrap={!isParagraph}
                sx={{flex: 1, minWidth: 0, whiteSpace: isParagraph ? 'pre-wrap' : undefined}}
                {...typographyProps}
            >
                {text || placeholder}
            </Typography>
            {hovered && (
                <EditOutlinedIcon
                    sx={{fontSize: 14, color: COLORS.grey[500], flexShrink: 0, mt: isParagraph ? 0.25 : 0}}
                />
            )}
        </Stack>
        {confirmDialog}
    </>);
});
